import { forEachObj } from 'remeda'

import { useRoute } from 'vue-router'
import { useIsAdmin } from '@/components/app/isAdmin'
import { useGtmVar } from '@/composables/app/gtm/useGtmVar'
import { getDocument } from '@/helpers/globals'
import { getAllCookies, useCookie } from '@/plugins/cookie-plugin'
import { useStoreMarketingDataMutation } from '@/composables/app/gtm/useUtmParams.generated.ts'
import { delay } from '@/helpers/delay'

const utmParamNames = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'gbraid',
    'wbraid',
    'fbclid',
    'fbp',
    'msclkid',
    'ttclid',
    'li_fat_id',
    'twclid',
    'epik',
    'rdt_cid',
    'dclid',
    'qclid',
] as const

export type UtmParamNames = (typeof utmParamNames)[number]
export type UtmParams = Record<UtmParamNames, string>

type GtmParams = {
    clientId: string
    sessionId: string
}

const GA_COOKIES_POLL_INTERVAL = 250
const GA_COOKIES_MAX_WAIT = 5000

function readGtmParams(document: Document): GtmParams {
    const allCookies = getAllCookies(document)

    const gtmParams = {
        clientId: allCookies['_ga'] || '',
        sessionId: '',
    }

    forEachObj(allCookies, (val, key) => {
        if (key.startsWith('_ga_')) {
            gtmParams.sessionId = val
        }
    })

    return gtmParams
}

/**
 * GA sets its _ga / _ga_<id> cookies only after GTM has loaded (and consent is given),
 * which can be after this composable runs. Poll for them, but never wait longer than
 * GA_COOKIES_MAX_WAIT so the UTM params are still stored when GA cookies never appear.
 */
async function waitForGtmParams(document: Document): Promise<GtmParams> {
    let gtmParams = readGtmParams(document)

    for (
        let waited = 0;
        waited < GA_COOKIES_MAX_WAIT &&
        !(gtmParams.clientId && gtmParams.sessionId);
        waited += GA_COOKIES_POLL_INTERVAL
    ) {
        await delay(GA_COOKIES_POLL_INTERVAL)

        gtmParams = readGtmParams(document)
    }

    return gtmParams
}

export async function useUtmParams(): Promise<void> {
    const document = getDocument()

    if (!document) {
        return
    }

    const isAdmin = useIsAdmin()

    if (isAdmin.value) {
        return
    }

    const areUtmParamsSetForSession = useCookie('areUtmParamsSetForSession')

    if (areUtmParamsSetForSession.value === 'true') {
        return
    }

    const utmParams = utmParamNames.reduce(
        (acc, paramName) => ({
            ...acc,
            [paramName]: '',
        }),
        {} as UtmParams
    )

    const query = useRoute().query

    // Store query params from UTM url
    forEachObj(utmParams, (_, paramName) => {
        const urlParam = query[paramName]?.toString()

        if (urlParam) {
            utmParams[paramName] = urlParam
        }
    })

    // Register GTM vars synchronously, before any await, while the setup context is still active.
    const clientId = useGtmVar<string>('client_id')
    const sessionId = useGtmVar<string>('session-id')
    const gtmCustomerId = useGtmVar('customer-id')

    const referrer = document.referrer
    const location = document.location.href

    const { executeMutation, data } = useStoreMarketingDataMutation()

    const gtmParams = await waitForGtmParams(document)

    if (gtmParams.clientId) {
        clientId.value = gtmParams.clientId
    }

    if (gtmParams.sessionId) {
        sessionId.value = gtmParams.sessionId
    }

    // GraphQL expects camelCase; FO2 collects snake_case URL keys.
    await executeMutation({
        input: {
            utmParams: {
                utmSource: utmParams.utm_source,
                utmMedium: utmParams.utm_medium,
                utmCampaign: utmParams.utm_campaign,
                utmTerm: utmParams.utm_term,
                utmContent: utmParams.utm_content,
                gclid: utmParams.gclid,
                gbraid: utmParams.gbraid,
                wbraid: utmParams.wbraid,
                fbclid: utmParams.fbclid,
                fbp: utmParams.fbp,
                msclkid: utmParams.msclkid,
                ttclid: utmParams.ttclid,
                liFatId: utmParams.li_fat_id,
                twclid: utmParams.twclid,
                epik: utmParams.epik,
                rdtCid: utmParams.rdt_cid,
                dclid: utmParams.dclid,
                qclid: utmParams.qclid,
            },
            gtmParams,
        },
        referrer,
        location,
    })

    const customerId = data.value?.storeMarketingData.customer?.id

    if (customerId) {
        gtmCustomerId.value = customerId
    }

    areUtmParamsSetForSession.value = 'true'
}
